import type {
  DssBlockStyles,
  DssBorder,
  DssBackground,
  DssComputedFragment,
  DssComputedOutput,
  DssCssStyles,
  DssFilter,
  DssPosition,
  DssShadowConfig,
  DssSpacing,
  DssStyles,
  DssText,
  DssTransform,
  DssTransition,
  DssTypography,
  StyleState,
} from "./types";

const VALID_SCOPED_CLASS = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const VALID_FONT_FAMILY = /^[a-zA-Z0-9\s,'"-]+$/;

let counter = 0;

function camelToKebab(value: string): string {
  return value.replace(/([A-Z])/g, "-$1").toLowerCase();
}

function sanitizeScopedClass(className: string): string {
  if (!VALID_SCOPED_CLASS.test(className) || className.length > 96) return "";
  return className;
}

function sanitizeCssValue(value: string): string {
  let safe = value;
  safe = safe.replace(/[{}]/g, "");
  safe = safe.replace(/\/\*/g, "");
  safe = safe.replace(/\*\//g, "");
  safe = safe.replace(/<script[\s\S]*?>/gi, "");
  safe = safe.replace(/<\/script>/gi, "");
  safe = safe.replace(/<style[\s\S]*?>/gi, "");
  safe = safe.replace(/<\/style>/gi, "");
  safe = safe.replace(/@import/gi, "");
  safe = safe.replace(/@charset/gi, "");
  safe = safe.replace(/expression\s*\(/gi, "");
  safe = safe.replace(/url\s*\(\s*["']?\s*javascript\s*:/gi, "");
  safe = safe.replace(/url\s*\(\s*["']?\s*data:\s*text\/html/gi, "");
  safe = safe.replace(/\\/g, "");
  return safe.trim();
}

export function generateScopedId(): string {
  counter += 1;
  const random = Math.random().toString(36).slice(2, 8);
  return `puck-el-${random}${counter}`;
}

export function resetScopedCounter(): void {
  counter = 0;
}

function sanitizeStyleMap(styles: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(styles)
      .map(([key, value]) => [key, sanitizeCssValue(value)] as const)
      .filter(([, value]) => value.length > 0),
  );
}

function cssPropsToString(styles: Record<string, string>): string {
  return Object.entries(styles)
    .map(([key, value]) => `${camelToKebab(key)}: ${sanitizeCssValue(value)}`)
    .join("; ");
}

function sanitizeFontFamily(fontFamily: string): string {
  if (!VALID_FONT_FAMILY.test(fontFamily)) {
    return "";
  }
  return sanitizeCssValue(fontFamily);
}

function valueWithUnit(value: number | string | undefined, unit = "px"): string {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "number") return `${value}${unit}`;
  return sanitizeCssValue(String(value));
}

function spacingValue(value: DssSpacing["padding"] | DssSpacing["margin"]): string {
  const unit = value.unit || "px";
  return [value.top, value.right, value.bottom, value.left]
    .map((part) => `${part}${unit}`)
    .join(" ");
}

function hexToRgb(hex: string): [number, number, number] | null {
  const normalized = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(normalized)) {
    return null;
  }

  const value = normalized.length === 3
    ? normalized.split("").map((char) => char + char).join("")
    : normalized;

  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

export function rgbaColor(color: string, opacityPercent: number | undefined): string {
  const safeColor = sanitizeCssValue(color);
  const opacity = opacityPercent === undefined ? 1 : Math.max(0, Math.min(100, opacityPercent)) / 100;

  const rgb = hexToRgb(safeColor);
  if (rgb) {
    return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${opacity})`;
  }

  if (opacity === 1) {
    return safeColor;
  }

  return safeColor;
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function buildGradient(styles: NonNullable<DssStyles["background"]>): string {
  const gradient = styles.gradient;
  const stops = gradient.stops.map((stop) => `${rgbaColor(stop.color, styles.opacity)} ${stop.position}%`).join(", ");

  switch (gradient.type) {
    case "radial":
      return `radial-gradient(${gradient.radialShape} at ${gradient.radialCenterX}% ${gradient.radialCenterY}%, ${stops})`;
    case "conic":
      return `conic-gradient(from ${gradient.angle}deg at ${gradient.radialCenterX}% ${gradient.radialCenterY}%, ${stops})`;
    case "linear":
    default:
      return `linear-gradient(${gradient.angle}deg, ${stops})`;
  }
}

function buildParticleField(color: string, accentColor: string, particleCount: number): string {
  const palette = [
    rgbaColor(color, 84),
    rgbaColor(accentColor, 76),
    rgbaColor(color, 58),
    rgbaColor(accentColor, 52),
  ];
  const layers = Array.from({ length: clampNumber(particleCount, 6, 30) }, (_, index) => {
    const x = (11 + ((index * 17) % 79));
    const y = (13 + ((index * 19) % 73));
    const size = 1 + (index % 3);
    const tone = palette[index % palette.length];
    return `radial-gradient(circle at ${x}% ${y}%, ${tone} 0 ${size}px, transparent ${size + 2}px)`;
  });

  return layers.join(", ");
}

export function buildBackgroundEffectCss(
  scopedClass: string,
  background: DssBackground | null | undefined,
): string {
  const safeClass = sanitizeScopedClass(scopedClass);
  const effect = background?.effect;
  if (!safeClass || !effect || effect.preset === "none") {
    return "";
  }

  const intensity = clampNumber(effect.intensity, 0, 100);
  const speed = clampNumber(effect.speed, 1, 10);
  const color = sanitizeCssValue(effect.color || "#2563eb");
  const accentColor = sanitizeCssValue(effect.accentColor || "#7c3aed");
  const blendMode = sanitizeCssValue(effect.blendMode || "soft-light");
  const animationSeconds = (12 - speed).toFixed(2);
  const overlayOpacity = (0.16 + intensity / 220).toFixed(3);
  const blurStrength = Math.round(18 + intensity * 0.4);
  const scaleBoost = (1 + intensity / 250).toFixed(3);
  const pseudoBase = [
    `.${safeClass}{position:relative;isolation:isolate}`,
    `.${safeClass}>*{position:relative;z-index:1}`,
    `.${safeClass}::before{content:"";position:absolute;inset:-14%;pointer-events:none;z-index:0;opacity:${overlayOpacity};will-change:transform,opacity;border-radius:inherit;mix-blend-mode:${blendMode}}`,
  ];

  let effectRule = "";
  let keyframes = "";

  switch (effect.preset) {
    case "gradient-shift":
      effectRule = `.${safeClass}::before{background-image:linear-gradient(135deg, ${color} 0%, ${accentColor} 45%, color-mix(in srgb, ${color} 42%, white) 100%);animation:easeBgGradientShift ${animationSeconds}s ease-in-out infinite}`;
      keyframes = `@keyframes easeBgGradientShift{0%{transform:translate3d(-6%,0,0) scale(1.04);opacity:${overlayOpacity}}50%{transform:translate3d(6%,-2%,0) scale(${scaleBoost});opacity:${(Number(overlayOpacity) + 0.06).toFixed(3)}}100%{transform:translate3d(-6%,0,0) scale(1.04);opacity:${overlayOpacity}}}`;
      break;
    case "particle-float":
      effectRule = `.${safeClass}::before{background-image:${buildParticleField(color, accentColor, effect.particleCount)};background-repeat:no-repeat;animation:easeBgParticleFloat ${animationSeconds}s ease-in-out infinite}`;
      keyframes = `@keyframes easeBgParticleFloat{0%{transform:translate3d(0,4%,0) scale(1);opacity:${overlayOpacity}}50%{transform:translate3d(0,-4%,0) scale(${scaleBoost});opacity:${(Number(overlayOpacity) + 0.08).toFixed(3)}}100%{transform:translate3d(0,4%,0) scale(1);opacity:${overlayOpacity}}}`;
      break;
    case "blob-morph":
      effectRule = `.${safeClass}::before{background-image:radial-gradient(circle at 28% 28%, ${rgbaColor(color, 78)} 0%, transparent 42%),radial-gradient(circle at 72% 64%, ${rgbaColor(accentColor, 72)} 0%, transparent 46%);filter:blur(${blurStrength}px);animation:easeBgBlobMorph ${animationSeconds}s ease-in-out infinite}`;
      keyframes = `@keyframes easeBgBlobMorph{0%{transform:translate3d(-3%,1%,0) scale(1);opacity:${overlayOpacity}}50%{transform:translate3d(4%,-2%,0) scale(${scaleBoost});opacity:${(Number(overlayOpacity) + 0.05).toFixed(3)}}100%{transform:translate3d(-3%,1%,0) scale(1);opacity:${overlayOpacity}}}`;
      break;
    case "aurora":
      effectRule = `.${safeClass}::before{background-image:linear-gradient(120deg, ${rgbaColor(color, 0)} 12%, ${rgbaColor(color, 64)} 36%, ${rgbaColor(accentColor, 72)} 62%, ${rgbaColor(accentColor, 0)} 86%);filter:blur(${blurStrength + 10}px);animation:easeBgAurora ${animationSeconds}s ease-in-out infinite}`;
      keyframes = `@keyframes easeBgAurora{0%{transform:translate3d(-6%,0,0) skewX(-8deg) scale(1.02)}50%{transform:translate3d(6%,-3%,0) skewX(8deg) scale(${scaleBoost})}100%{transform:translate3d(-6%,0,0) skewX(-8deg) scale(1.02)}}`;
      break;
    case "mesh-gradient":
      effectRule = `.${safeClass}::before{background-image:radial-gradient(circle at 18% 24%, ${rgbaColor(color, 70)} 0%, transparent 34%),radial-gradient(circle at 78% 18%, ${rgbaColor(accentColor, 68)} 0%, transparent 30%),radial-gradient(circle at 62% 78%, color-mix(in srgb, ${accentColor} 65%, white) 0%, transparent 38%);filter:blur(${blurStrength}px);animation:easeBgMesh ${animationSeconds}s ease-in-out infinite}`;
      keyframes = `@keyframes easeBgMesh{0%{transform:translate3d(0,0,0) scale(1)}50%{transform:translate3d(0,-3%,0) scale(${scaleBoost})}100%{transform:translate3d(0,0,0) scale(1)}}`;
      break;
    default:
      return "";
  }

  return [
    ...pseudoBase,
    effectRule,
    keyframes,
    `@media (prefers-reduced-motion: reduce){.${safeClass}::before{animation:none!important;transform:none!important}}`,
  ].join("\n");
}

export function computeBackground(background: DssStyles["background"]): DssComputedFragment {
  if (!background || background.mode === "none") {
    return emptyFragment();
  }

  const inlineStyles: Record<string, string> = {};
  const wrapperAttrs: Record<string, string> = {};

  if (background.mode === "color" && background.color) {
    inlineStyles.background = rgbaColor(background.color, background.opacity);
  }

  if (background.mode === "gradient" && background.gradient.stops.length > 0) {
    inlineStyles.background = sanitizeCssValue(buildGradient(background));
  }

  if (background.mode === "image" && background.image.url) {
    inlineStyles.backgroundImage = `url(${sanitizeCssValue(background.image.url)})`;
    inlineStyles.backgroundPosition = `${background.image.positionX}% ${background.image.positionY}%`;
    inlineStyles.backgroundSize = background.image.size === "custom"
      ? `${sanitizeCssValue(background.image.customWidth)} ${sanitizeCssValue(background.image.customHeight)}`
      : sanitizeCssValue(background.image.size);
    inlineStyles.backgroundRepeat = sanitizeCssValue(background.image.repeat);
    inlineStyles.backgroundAttachment = sanitizeCssValue(background.image.attachment);
  }

  if (background.mode === "video" && background.video.url) {
    wrapperAttrs["data-dss-video"] = sanitizeCssValue(background.video.url);
    wrapperAttrs["data-dss-video-autoplay"] = String(Boolean(background.video.autoplay));
    wrapperAttrs["data-dss-video-loop"] = String(Boolean(background.video.loop));
    wrapperAttrs["data-dss-video-muted"] = String(Boolean(background.video.muted));
    wrapperAttrs.preload = "none";
  }

  if (background.effect && background.effect.preset !== "none") {
    inlineStyles.position = inlineStyles.position ?? "relative";
    inlineStyles.isolation = "isolate";
  }

  return {
    inlineStyles: sanitizeStyleMap(inlineStyles),
    cssRules: "",
    wrapperAttrs,
  };
}

export function computeTypography(typography: DssTypography | undefined): DssComputedFragment {
  if (!typography) {
    return emptyFragment();
  }

  const inlineStyles: Record<string, string> = {};
  const fontFamily = sanitizeFontFamily(typography.fontFamily);
  if (fontFamily) inlineStyles.fontFamily = fontFamily;
  if (typography.fontSize) inlineStyles.fontSize = `${typography.fontSize}${typography.fontSizeUnit}`;
  if (typography.fontWeight) inlineStyles.fontWeight = String(typography.fontWeight);
  if (typography.fontStyle && typography.fontStyle !== "normal") inlineStyles.fontStyle = sanitizeCssValue(typography.fontStyle);
  if (typography.textTransform && typography.textTransform !== "none") inlineStyles.textTransform = sanitizeCssValue(typography.textTransform);
  if (typography.textDecoration && typography.textDecoration !== "none") inlineStyles.textDecoration = sanitizeCssValue(typography.textDecoration);
  if (typography.lineHeight) inlineStyles.lineHeight = `${typography.lineHeight}${typography.lineHeightUnit}`;
  if (typography.letterSpacing) inlineStyles.letterSpacing = `${typography.letterSpacing}${typography.letterSpacingUnit}`;
  if (typography.wordSpacing) inlineStyles.wordSpacing = `${typography.wordSpacing}px`;

  return {
    inlineStyles: sanitizeStyleMap(inlineStyles),
    cssRules: "",
    wrapperAttrs: {},
  };
}

export function computeText(text: DssText | undefined): DssComputedFragment {
  if (!text) {
    return emptyFragment();
  }

  return {
    inlineStyles: sanitizeStyleMap({
      ...(text.color ? { color: text.color } : {}),
      ...(text.align ? { textAlign: text.align } : {}),
    }),
    cssRules: "",
    wrapperAttrs: {},
  };
}

export function computeSpacing(spacing: DssSpacing | undefined): DssComputedFragment {
  if (!spacing) {
    return emptyFragment();
  }

  return {
    inlineStyles: sanitizeStyleMap({
      padding: spacingValue(spacing.padding),
      margin: spacingValue(spacing.margin),
    }),
    cssRules: "",
    wrapperAttrs: {},
  };
}

export function computeBorder(border: DssBorder | undefined): DssComputedFragment {
  if (!border) {
    return emptyFragment();
  }

  const inlineStyles: Record<string, string> = {
    borderStyle: sanitizeCssValue(border.style),
    borderColor: sanitizeCssValue(border.color),
    borderTopWidth: `${border.widthTop}px`,
    borderRightWidth: `${border.widthRight}px`,
    borderBottomWidth: `${border.widthBottom}px`,
    borderLeftWidth: `${border.widthLeft}px`,
    borderTopLeftRadius: `${border.radiusTopLeft}px`,
    borderTopRightRadius: `${border.radiusTopRight}px`,
    borderBottomRightRadius: `${border.radiusBottomRight}px`,
    borderBottomLeftRadius: `${border.radiusBottomLeft}px`,
  };

  return {
    inlineStyles: sanitizeStyleMap(inlineStyles),
    cssRules: "",
    wrapperAttrs: {},
  };
}

export function computeShadow(shadow: DssStyles["shadow"]): DssComputedFragment {
  if (!shadow) {
    return emptyFragment();
  }

  const config: DssShadowConfig = typeof shadow === "string" ? { box: shadow } : shadow;
  const inlineStyles: Record<string, string> = {};
  if (config.box) inlineStyles.boxShadow = config.box;
  if (config.text) inlineStyles.textShadow = config.text;

  return {
    inlineStyles: sanitizeStyleMap(inlineStyles),
    cssRules: "",
    wrapperAttrs: {},
  };
}

export function computeTransform(transform: DssTransform | undefined): DssComputedFragment {
  if (!transform) {
    return emptyFragment();
  }

  const parts = [
    transform.rotate ? `rotate(${sanitizeCssValue(transform.rotate)})` : "",
    transform.scale ? `scale(${sanitizeCssValue(transform.scale)})` : "",
    transform.scaleX ? `scaleX(${sanitizeCssValue(transform.scaleX)})` : "",
    transform.scaleY ? `scaleY(${sanitizeCssValue(transform.scaleY)})` : "",
    transform.translateX ? `translateX(${sanitizeCssValue(transform.translateX)})` : "",
    transform.translateY ? `translateY(${sanitizeCssValue(transform.translateY)})` : "",
    transform.skewX ? `skewX(${sanitizeCssValue(transform.skewX)})` : "",
    transform.skewY ? `skewY(${sanitizeCssValue(transform.skewY)})` : "",
  ].filter(Boolean);

  return {
    inlineStyles: sanitizeStyleMap({
      ...(parts.length > 0 ? { transform: parts.join(" ") } : {}),
      ...(transform.transformOrigin ? { transformOrigin: transform.transformOrigin } : {}),
    }),
    cssRules: "",
    wrapperAttrs: {},
  };
}

export function computeFilter(filter: DssFilter | undefined): DssComputedFragment {
  if (!filter) {
    return emptyFragment();
  }

  const parts = [
    filter.blur ? `blur(${sanitizeCssValue(filter.blur)})` : "",
    filter.brightness ? `brightness(${sanitizeCssValue(filter.brightness)})` : "",
    filter.contrast ? `contrast(${sanitizeCssValue(filter.contrast)})` : "",
    filter.grayscale ? `grayscale(${sanitizeCssValue(filter.grayscale)})` : "",
    filter.hueRotate ? `hue-rotate(${sanitizeCssValue(filter.hueRotate)})` : "",
    filter.invert ? `invert(${sanitizeCssValue(filter.invert)})` : "",
    filter.opacity ? `opacity(${sanitizeCssValue(filter.opacity)})` : "",
    filter.saturate ? `saturate(${sanitizeCssValue(filter.saturate)})` : "",
    filter.sepia ? `sepia(${sanitizeCssValue(filter.sepia)})` : "",
  ].filter(Boolean);

  return {
    inlineStyles: sanitizeStyleMap({
      ...(parts.length > 0 ? { filter: parts.join(" ") } : {}),
      ...(filter.mixBlendMode ? { mixBlendMode: filter.mixBlendMode } : {}),
    }),
    cssRules: "",
    wrapperAttrs: {},
  };
}

export function computePosition(position: DssPosition | undefined): DssComputedFragment {
  if (!position) {
    return emptyFragment();
  }

  return {
    inlineStyles: sanitizeStyleMap({
      ...(position.display ? { display: position.display } : {}),
      ...(position.position ? { position: position.position } : {}),
      ...(position.top ? { top: position.top } : {}),
      ...(position.right ? { right: position.right } : {}),
      ...(position.bottom ? { bottom: position.bottom } : {}),
      ...(position.left ? { left: position.left } : {}),
      ...(position.zIndex !== undefined ? { zIndex: String(position.zIndex) } : {}),
    }),
    cssRules: "",
    wrapperAttrs: {},
  };
}

export function computeOpacity(opacity: string | undefined): DssComputedFragment {
  if (!opacity) {
    return emptyFragment();
  }

  return {
    inlineStyles: sanitizeStyleMap({ opacity }),
    cssRules: "",
    wrapperAttrs: {},
  };
}

function computeTransitionCss(transition: DssTransition | undefined): string | undefined {
  if (!transition) {
    return undefined;
  }

  const property = sanitizeCssValue(transition.property ?? "all");
  const duration = sanitizeCssValue(transition.duration ?? "200ms");
  const easing = sanitizeCssValue(transition.easing ?? "ease");
  return `${property} ${duration} ${easing}`;
}

function mergeFragments(fragments: readonly DssComputedFragment[]): DssComputedFragment {
  return fragments.reduce<DssComputedFragment>(
    (result, fragment) => ({
      inlineStyles: {
        ...result.inlineStyles,
        ...fragment.inlineStyles,
      },
      cssRules: [result.cssRules, fragment.cssRules].filter(Boolean).join("\n"),
      wrapperAttrs: {
        ...result.wrapperAttrs,
        ...fragment.wrapperAttrs,
      },
    }),
    emptyFragment(),
  );
}

function emptyFragment(): DssComputedFragment {
  return {
    inlineStyles: {},
    cssRules: "",
    wrapperAttrs: {},
  };
}

function computeBaseFragment(dss: Partial<DssStyles>): DssComputedFragment {
  return mergeFragments([
    computeBackground(dss.background),
    computeTypography(dss.typography),
    computeText(dss.text),
    computeSpacing(dss.spacing),
    computeBorder(dss.border),
    computeShadow(dss.shadow),
    computeTransform(dss.transform),
    computeFilter(dss.filter),
    computePosition(dss.position),
    computeOpacity(dss.opacity),
    {
      inlineStyles: sanitizeStyleMap({
        ...(dss.animation?.transform ? { transform: dss.animation.transform } : {}),
        ...(dss.animation?.transition ? { transition: dss.animation.transition } : {}),
        ...(dss.animation?.duration ? { animationDuration: dss.animation.duration } : {}),
        ...(computeTransitionCss(dss.transition) ? { transition: computeTransitionCss(dss.transition)! } : {}),
      }),
      cssRules: "",
      wrapperAttrs: {},
    },
  ]);
}

function stateStyleOverrides(dss: Partial<DssStyles>): Record<string, string> {
  return computeBaseFragment(dss).inlineStyles;
}

function generateStateCss(
  scopedClass: string,
  states: DssStyles["states"],
  transition: DssTransition | undefined,
): string {
  const safeClass = sanitizeScopedClass(scopedClass);
  if (!safeClass || !states) return "";

  const hoverStyle = states.hover ? stateStyleOverrides(states.hover) : {};
  const focusStyle = states.focus ? stateStyleOverrides(states.focus) : {};
  const activeStyle = states.active ? stateStyleOverrides(states.active) : {};

  if (
    Object.keys(hoverStyle).length === 0
    && Object.keys(focusStyle).length === 0
    && Object.keys(activeStyle).length === 0
  ) {
    return "";
  }

  const rules: string[] = [];
  const transitionCss = computeTransitionCss(transition);
  if (transitionCss) {
    rules.push(`.${safeClass} { transition: ${transitionCss}; }`);
  }

  if (Object.keys(hoverStyle).length > 0) {
    rules.push(`.${safeClass}:hover { ${cssPropsToString(hoverStyle)}; }`);
  }

  if (Object.keys(focusStyle).length > 0) {
    rules.push(`.${safeClass}:focus { ${cssPropsToString(focusStyle)}; }`);
  }

  if (Object.keys(activeStyle).length > 0) {
    rules.push(`.${safeClass}:active { ${cssPropsToString(activeStyle)}; }`);
  }

  return rules.join("\n");
}

function responsiveRule(
  scopedClass: string,
  breakpoint: number,
  dss: Partial<DssStyles> | undefined,
  visibilityRule?: string,
): string {
  if (!dss && !visibilityRule) return "";

  const fragment = dss ? computeBaseFragment(dss) : emptyFragment();
  const declarations = cssPropsToString(fragment.inlineStyles);
  const rules = [
    declarations ? `.${scopedClass} { ${declarations}; }` : "",
    visibilityRule ?? "",
  ].filter(Boolean);

  if (rules.length === 0) return "";

  return `@media (max-width: ${breakpoint}px) {\n${rules.join("\n")}\n}`;
}

function desktopVisibilityRule(scopedClass: string, hide: boolean | undefined): string {
  if (!hide) return "";
  return `@media (min-width: 769px) {\n.${scopedClass} { display: none !important; }\n}`;
}

function computeResponsiveCss(scopedClass: string, dss: DssStyles): string {
  const safeClass = sanitizeScopedClass(scopedClass);
  if (!safeClass) return "";

  const rules = [
    desktopVisibilityRule(safeClass, dss.visibility?.hideOnDesktop),
    responsiveRule(
      safeClass,
      768,
      dss.responsive?.tablet,
      dss.visibility?.hideOnTablet ? `.${safeClass} { display: none !important; }` : undefined,
    ),
    responsiveRule(
      safeClass,
      480,
      dss.responsive?.mobile,
      dss.visibility?.hideOnMobile ? `.${safeClass} { display: none !important; }` : undefined,
    ),
  ].filter(Boolean);

  return rules.join("\n");
}

export interface ComputeDssStylesOptions {
  scopedClass?: string;
}

export function computeDssStyles(
  dss: DssStyles | undefined,
  options?: ComputeDssStylesOptions,
): DssComputedOutput {
  const scopedClass = options?.scopedClass ? sanitizeScopedClass(options.scopedClass) || generateScopedId() : generateScopedId();
  if (!dss || Object.keys(dss).length === 0) {
    return {
      inlineStyles: {},
      cssRules: "",
      scopedClass,
      wrapperAttrs: {},
    };
  }

  const base = computeBaseFragment(dss);
  const cssRules = [
    buildBackgroundEffectCss(scopedClass, dss.background),
    generateStateCss(scopedClass, dss.states, dss.transition),
    computeResponsiveCss(scopedClass, dss),
  ].filter(Boolean).join("\n");

  return {
    inlineStyles: base.inlineStyles,
    cssRules,
    scopedClass,
    wrapperAttrs: base.wrapperAttrs,
  };
}

export function computeStyles(
  blockStyles: DssBlockStyles,
  state: StyleState = "normal",
): Partial<DssStyles> {
  const base = blockStyles.states.normal ?? {};
  if (state === "normal") return base;

  const override = blockStyles.states[state] ?? {};
  return { ...base, ...override };
}

export function dssToCSS(styles: Partial<DssCssStyles>): Record<string, string> {
  return sanitizeStyleMap(styles as Record<string, string>);
}
